import {
  ApolloLink,
  createHttpLink,
  type FetchResult,
  Observable,
  type Observer,
  type Operation,
} from '@apollo/client';
import {onError} from '@apollo/client/link/error';
import {RetryLink} from '@apollo/client/link/retry';
import {getCookie} from '@wandb/weave/common/util/cookie';
import * as queryString from 'query-string';
import {type Dispatch, type Store} from 'redux';

import {backendHost} from '../config';
import {captureError} from '../integrations';
import {displayError, displayErrorPortal} from '../state/global/actions';
import {getPerfTimingLink} from './getPerfTimingLink';
import {getWandbTraceId} from './getWandbTraceId';
import {isOnReportView} from './urls';

// These global vars are set by `createCircularDependencies` below :P
// There should definitely be a better way of doing this.
let dispatch: Dispatch;
// auth is from auth.js and is currently untyped
let auth: any = null;

const apolloEndpoint = `${backendHost()}/graphql2`;

const httpLink = createHttpLink({
  fetch: (input: URL | RequestInfo, init?: RequestInit): Promise<Response> => {
    // we force using window.fetch because we need to use datadog's updated instance of window.fetch
    return window.fetch(input, init);
  },
  uri: apolloEndpoint,
  // Our credentials may be a cookie on a different domain to backend
  // `api.wandb.ai` vs `wandb.ai`
  credentials: 'include',
});

// Adds a header specifying that an admin wants to use their
// admin privileges based on a cookie that they
// can manually toggle.
const useAdminPrivilegesMiddleware = new ApolloLink((operation, forward) => {
  const useAdminPrivileges = getCookie('use_admin_privileges') === 'true';
  if (useAdminPrivileges) {
    setHeader(operation, 'use-admin-privileges', 'true');
  }
  return forward!(operation);
});

const authMiddleware = new ApolloLink((operation, forward) => {
  const safeForward = forward!;
  return new Observable<FetchResult>((observer: Observer<FetchResult>) => {
    auth.ensureCurrentIdToken().then((t: string | null) => {
      // The signup flow accepts a token
      const qs = queryString.parse(window.location.search);
      const token = qs.token || t;
      const accessToken = qs.accessToken;

      setHeader(operation, 'X-Origin', window.location.origin);

      if (token) {
        setHeader(operation, 'authorization', `Bearer ${token}`);
      }

      if (accessToken && isOnReportView()) {
        setHeader(operation, 'access-token', accessToken as string);
      }
      if (isOnReportView()) {
        setHeader(operation, 'report-view', 'true');
      }

      // [Deprecated] Anonymous API keys are disabled, but old links may still
      // contain ?apiKey=... in the URL. Scrub it for safety, even though we can't use that key.
      if (qs.apiKey) {
        const newQueryString = {...qs};
        delete newQueryString.apiKey;
        window.location.search = queryString.stringify(newQueryString);
      }

      safeForward(operation).subscribe(observer);
    });
  });
});

const errorLink = onError(
  ({operation, response, networkError, graphQLErrors}) => {
    // NOTE: Don't accidentally return something from this function! You'll
    // potentially get an error that says "retriedResult.subscribe is not a
    // function" because you're not returning an observable.

    console.log({operation, response, networkError, graphQLErrors});

    const traceId = getWandbTraceId(networkError, operation);

    // Regardless of propagateErrors, we need to bubble up 401's so the frontend
    // can prompt us to login.
    if (
      // eslint-disable-next-line no-extra-boolean-cast
      !Boolean(operation.getContext().noLogin) &&
      networkError &&
      (networkError as any).statusCode === 401
    ) {
      dispatch(displayErrorPortal('Session expired. Forcing login...'));
      dispatch(
        displayError({
          code: 401,
          message: networkError.message,
          traceId,
        })
      );
      return;
    }

    // When propagateErrors is set on the context, the query issuer wants to
    // handle errors manually so abort processing.
    // eslint-disable-next-line no-extra-boolean-cast
    if (Boolean(operation.getContext().propagateErrors)) {
      return;
    }

    if (graphQLErrors) {
      // Capture GraphQL errors
      graphQLErrors.forEach(({message, locations, path}) => {
        captureError(`[GraphQL Error] ${message}`, 'apollo_graphql_error', {
          level: 'info',
          extra: {
            operation: operation.operationName,
            path,
            locations,
          },
        });
      });
      return;
    }
  }
);

// Note apollo is a POS and doesn't consistently return the error object.
// We check for the case where error.statusCode is not present, which typically
// means a request failed in a retryable way (for us).
// https://github.com/apollographql/apollo-link/issues/300
const retryLink = new RetryLink({
  // This setup will retry for
  //   sum_i:0-6(200*2**i) = ~25s (but with jitter it can be between 0 and 50s)
  delay: {
    initial: 200,
    max: 100000,
    jitter: true,
  },
  attempts: {
    max: 7,
    retryIf: (error, operation) => {
      if (operation.getContext().doNotRetry) {
        return false;
      }

      // Only retry internal errors
      return (
        // TODO(adrian): We should probably not be retrying 500s.
        // It's safer for us to aggressively retry like this but it
        // causes lots of spurious requests and makes a mess in the
        // logs and in Sentry.
        (error && error.statusCode && error.statusCode >= 500) ||
        // Happens when the fetch API receives a rejected promise, which happens on
        // temporary server failures sometimes.
        (error && error.statusCode == null) ||
        error.message === 'Unexpected token < in JSON at position 0'
      );
    },
  },
});

export const apolloLink = ApolloLink.from([
  useAdminPrivilegesMiddleware,
  authMiddleware,
  errorLink,
  retryLink,
  // Right now these queries don't have an operation name since
  // they are user defined.
  getPerfTimingLink('CustomChartQuery:'),
  httpLink,
]);

export const createCircularDependencies = (store: Store, authIn: any) => {
  dispatch = store.dispatch;
  auth = authIn;
};

interface OperationContext {
  headers?: {[key: string]: string};
}

function setHeader(op: Operation, key: string, value: string) {
  op.setContext(({headers = {}}: OperationContext) => ({
    headers: {
      ...headers,
      [key]: value,
    },
  }));
}
